import React, { useContext, useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import Paginator from '../../components/ui/Paginator';
import { AuthContext } from '../../context/auth';
import { errorMessage, isPlatformUser } from '../../utils/access';
import { CatalogFeature, featureIcon, formatDay, formatMoney, formatTotals, MerchantWithFeatures, MonthlyTotal, ORIGIN_LABEL } from './featureUi';

const PAGE_SIZE = 20;

const USAGE_LABEL: Record<string, string> = { 'electronic-invoice': 'documentos emitidos', accounting: 'facturas leídas' };

const STATUS_LABEL: Record<string, { label: string; css: string }> = {
    ACT: { label: 'Activo', css: 'bg-success-subtle text-success-emphasis' },
    INA: { label: 'Inactivo', css: 'bg-secondary-subtle text-secondary-emphasis' },
    DEL: { label: 'Eliminado', css: 'bg-danger-subtle text-danger-emphasis' },
};

// Sumatoria sobre todos los comercios del filtro: lo que pagan por cada funcionalidad (precio al comercio)
interface SummaryLine { code: string; name: string; currency: string | null; merchants: number; cancelled: number; amount: number; renewing: number; }
interface Summary { lines: SummaryLine[]; totals: MonthlyTotal[]; nextMonthTotals: MonthlyTotal[]; }

interface ListResponse { merchants: MerchantWithFeatures[]; total: number; summary?: Summary; }
interface BankOption { id: number; name: string; }

// Comercios del banco con sus funcionalidades activas (usuarios de banco; la plataforma puede elegir el banco)
export const MerchantFeaturesPage = () => {
    const { user } = useContext(AuthContext);
    const isPlatform = isPlatformUser(user);

    const [catalog, setCatalog] = useState<CatalogFeature[]>([]);
    const [banks, setBanks] = useState<BankOption[]>([]);
    const [filters, setFilters] = useState({ search: '', feature: '', status: 'ACT', bankId: '' });
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<ListResponse>({ merchants: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState<number | null>(null);

    useEffect(() => {
        topApi.get<{ features: CatalogFeature[] }>('/features/catalog').then(resp => setCatalog(resp.data.features)).catch(() => undefined);
        if (isPlatform) {
            topApi.get<{ banks: { id: number; name: string }[] }>('/bank/getBanks')
                .then(resp => setBanks((resp.data.banks ?? []).map(b => ({ id: b.id, name: b.name }))))
                .catch(() => undefined);
        }
    }, [isPlatform]);

    // Búsqueda con espera para no consultar en cada tecla
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => (prev.search === search.trim() ? prev : { ...prev, search: search.trim() }));
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setLoading(true);
        setError('');
        topApi.get<ListResponse>('/features/merchants', {
            params: {
                pageIndex: page - 1, pageSize: PAGE_SIZE,
                search: filters.search || undefined, feature: filters.feature || undefined,
                status: filters.status || undefined, bankId: filters.bankId || undefined,
            },
        })
            .then(resp => setData({ merchants: resp.data.merchants ?? [], total: resp.data.total ?? 0, summary: resp.data.summary }))
            .catch(e => { setError(errorMessage(e)); setData({ merchants: [], total: 0 }); })
            .finally(() => setLoading(false));
    }, [filters, page]);

    const setFilter = (field: 'feature' | 'status' | 'bankId') => (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFilters(prev => ({ ...prev, [field]: e.target.value }));
        setPage(1);
    };

    const totalPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="mb-4">
                <h3 className="mb-1">Funcionalidades por comercio</h3>
                <p className="text-muted mb-0">
                    Comercios asociados {isPlatform ? 'a cada banco' : 'a su banco'}, las funcionalidades que tienen activas y lo que pagan este mes.
                    Facturación electrónica y contabilidad se cobran por cada bloque de uso del mes.
                </p>
            </div>

            {/* SUMATORIA (todos los comercios del filtro) */}
            {data.summary && !loading && (() => {
                const summary = data.summary;
                const changes = formatTotals(summary.nextMonthTotals) !== formatTotals(summary.totals);
                return (<>
                    <div className="row g-3 mb-3">
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100"><div className="card-body">
                                <div className="text-muted small">Comercios</div>
                                <div className="fs-4 fw-semibold">{data.total}</div>
                            </div></div>
                        </div>
                        <div className="col-sm-6 col-lg-3">
                            <div className="card border-0 shadow-sm h-100"><div className="card-body">
                                <div className="text-muted small">Pagan este mes</div>
                                <div className="fs-4 fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{summary.totals.length ? formatTotals(summary.totals) : '-'}</div>
                            </div></div>
                        </div>
                        {changes && (
                            <div className="col-sm-6 col-lg-3">
                                <div className="card border-0 shadow-sm h-100"><div className="card-body">
                                    <div className="text-muted small">Desde el próximo mes</div>
                                    <div className="fs-4 fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{summary.nextMonthTotals.length ? formatTotals(summary.nextMonthTotals) : '-'}</div>
                                    <div className="small text-muted">Sin las cancelaciones</div>
                                </div></div>
                            </div>
                        )}
                    </div>

                    {/* {summary.lines.length > 0 && (
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body">
                                <h6 className="mb-3">Por funcionalidad</h6>
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead>
                                            <tr className="text-muted small text-uppercase">
                                                <th>Funcionalidad</th>
                                                <th className="text-end">Comercios</th>
                                                <th className="text-end">Pagan este mes</th>
                                                {changes && <th className="text-end">Próximo mes</th>}
                                            </tr>
                                        </thead>
                                        <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {summary.lines.map(line => (
                                                <tr key={`${line.code}-${line.currency}`}>
                                                    <td><i className={`bi ${featureIcon(line.code)} me-2 text-primary`} />{line.name}</td>
                                                    <td className="text-end">
                                                        {line.merchants}
                                                        {line.cancelled > 0 && <span className="small text-warning-emphasis ms-1">({line.cancelled} cancelada{line.cancelled > 1 ? 's' : ''})</span>}
                                                    </td>
                                                    <td className="text-end">{formatMoney(line.amount, line.currency)}</td>
                                                    {changes && <td className="text-end">{formatMoney(line.renewing, line.currency)}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan={2} className="text-end">Total</th>
                                                <th className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTotals(summary.totals)}</th>
                                                {changes && <th className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTotals(summary.nextMonthTotals)}</th>}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )} */}
                </>);
            })()}

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className={isPlatform ? 'col-md-4' : 'col-md-6'}>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" type="text" placeholder="Buscar por nombre o identificación..." aria-label="Buscar comercio"
                                    value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                        {isPlatform && (
                            <div className="col-md-3">
                                <select className="form-select" aria-label="Banco" value={filters.bankId} onChange={setFilter('bankId')}>
                                    <option value="">Todos los bancos</option>
                                    {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            </div>
                        )}
                        <div className={isPlatform ? 'col-md-3' : 'col-md-4'}>
                            <select className="form-select" aria-label="Funcionalidad" value={filters.feature} onChange={setFilter('feature')}>
                                <option value="">Todas las funcionalidades</option>
                                {catalog.map(f => <option key={f.code} value={f.code}>Con {f.name.toLowerCase()}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select" aria-label="Estado" value={filters.status} onChange={setFilter('status')}>
                                <option value="">Todos los estados</option>
                                <option value="ACT">Activos</option>
                                <option value="INA">Inactivos</option>
                            </select>
                        </div>
                    </div>

                    {error && <div className="alert alert-danger small">{error}</div>}

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Comercio</th>
                                    <th>Funcionalidades</th>
                                    <th className="text-end">Total del mes</th>
                                    <th>Estado</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={5} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</td></tr>
                                ) : data.merchants.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center text-muted py-4">No hay comercios con esos filtros.</td></tr>
                                ) : data.merchants.map(m => {
                                    const status = STATUS_LABEL[m.status] ?? { label: m.status, css: 'bg-secondary-subtle' };
                                    const open = expanded === m.id;
                                    return (
                                        <React.Fragment key={m.id}>
                                            <tr>
                                                <td>
                                                    <div className="fw-medium">{m.name}</div>
                                                    <div className="small text-muted">
                                                        {[m.documentNumber, m.parent ? `Sucursal de ${m.parent.name ?? m.parent.id}` : null].filter(Boolean).join(' · ')}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {m.features.length === 0 && <span className="text-muted small">Sin registro</span>}
                                                        {m.features.map(f => (
                                                            <span key={f.code} title={f.activeUntil ? `Cancelada, vigente hasta el ${formatDay(f.activeUntil)}` : undefined}
                                                                className={`badge rounded-pill fw-normal ${f.activeUntil ? 'bg-warning-subtle text-warning-emphasis' : 'bg-primary-subtle text-primary-emphasis'}`}>
                                                                <i className={`bi ${f.activeUntil ? 'bi-hourglass-split' : featureIcon(f.code)} me-1`} />{f.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="text-end text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTotals(m.monthlyTotal)}</td>
                                                <td><span className={`badge rounded-pill ${status.css}`}>{status.label}</span></td>
                                                <td className="text-end">
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" aria-expanded={open}
                                                        title={open ? 'Ocultar detalle' : 'Ver detalle'} onClick={() => setExpanded(open ? null : m.id)}>
                                                        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                                                    </button>
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr className="table-light">
                                                    <td colSpan={5}>
                                                        <table className="table table-sm mb-0 bg-transparent">
                                                            <thead>
                                                                <tr className="small text-muted">
                                                                    <th>Funcionalidad</th><th>Activada</th><th>Origen</th><th>Vigencia</th><th className="text-end">Precio</th><th>Uso del mes</th><th className="text-end">Este mes</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {m.features.map(f => (
                                                                    <tr key={f.code} className="small">
                                                                        <td><i className={`bi ${featureIcon(f.code)} me-2 text-primary`} />{f.name}</td>
                                                                        <td>{f.activatedAt ? new Date(f.activatedAt).toLocaleDateString('es-CR') : '-'}</td>
                                                                        <td>{ORIGIN_LABEL[f.origin ?? ''] ?? f.origin ?? '-'}</td>
                                                                        <td>{f.activeUntil ? <span className="text-warning-emphasis">Cancelada, hasta el {formatDay(f.activeUntil)}</span> : 'Se renueva cada mes'}</td>
                                                                        <td className="text-end text-nowrap">
                                                                            {formatMoney(f.monthlyPrice, f.currency)}
                                                                            {f.blockSize !== undefined && <div className="text-muted">por bloque de {f.blockSize.toLocaleString('es-CR')}</div>}
                                                                        </td>
                                                                        <td>
                                                                            {f.blocks !== undefined
                                                                                ? <>{(f.usage ?? 0).toLocaleString('es-CR')} {USAGE_LABEL[f.code] ?? ''}<div className="text-muted">{f.blocks} bloque{f.blocks > 1 ? 's' : ''}</div></>
                                                                                : <span className="text-muted">-</span>}
                                                                        </td>
                                                                        <td className="text-end text-nowrap">{formatMoney(f.amount ?? f.monthlyPrice, f.currency)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span className="small text-muted">{data.total} comercio(s)</span>
                        {totalPage > 1 && <Paginator totalPage={totalPage} pageActual={page} onChange={setPage} />}
                    </div>
                </div>
            </div>
        </div>
    );
};
